import React, { useState, useCallback } from 'react';
import { Container, Form } from 'react-bootstrap';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import CompanyLayout from '../common/CompanyLayout';

// ─── Types ───────────────────────────────────────────────────────────────────

interface FlourItem {
  id: number;
  name: string;
  bakerPct: number;
}

interface OtherItem {
  id: number;
  name: string;
  bakerPct: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

let nextId = 10;
const uid = () => ++nextId;

const inputBox: React.CSSProperties = {
  border: '1.5px solid #e2e8f0',
  borderRadius: '10px',
  background: '#fff',
  fontSize: '1.05rem',
  fontWeight: 700,
  color: '#1e293b',
  textAlign: 'center',
  padding: '10px 8px',
  width: '100%',
  outline: 'none',
};

const readonlyBox: React.CSSProperties = {
  ...inputBox,
  background: '#f8fafc',
  border: '1.5px solid #f1f5f9',
  color: '#475569',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  color: '#94a3b8',
  fontWeight: 600,
  marginBottom: 6,
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function BakersCalculator() {
  const [individualWeight, setIndividualWeight] = useState<number>(1000);
  const [qty, setQty] = useState<number>(1);
  const [flours, setFlours] = useState<FlourItem[]>([{ id: 1, name: 'Flour', bakerPct: 100 }]);
  const [others, setOthers] = useState<OtherItem[]>([
    { id: 2, name: 'Starter', bakerPct: 0 },
    { id: 3, name: 'Water', bakerPct: 0 },
    { id: 4, name: 'Salt', bakerPct: 0 },
  ]);
  const [editMode, setEditMode] = useState(false);

  // ── Computed ──────────────────────────────────────────────────────────────

  const totalDoughWeight = individualWeight * qty;

  // sum of ALL baker percentages (flour + others)
  const totalFlourBakerPct = flours.reduce((s, f) => s + (f.bakerPct || 0), 0);
  const totalAllBakerPct =
    totalFlourBakerPct + others.reduce((s, o) => s + (o.bakerPct || 0), 0);

  // grams per ingredient = baker_pct / totalAllBakerPct × totalDoughWeight
  const gramsFor = (bakerPct: number) =>
    totalAllBakerPct > 0
      ? (bakerPct / totalAllBakerPct) * totalDoughWeight
      : 0;

  // Total hydration = sum of baker% for all "wet" other ingredients
  // (water, starter, levain, poolish, biga, milk, cream)
  const wetKeywords = ['water', 'starter', 'levain', 'poolish', 'biga', 'milk', 'cream'];
  const totalHydration =
    totalFlourBakerPct > 0
      ? others
          .filter(o => wetKeywords.some(kw => o.name.toLowerCase().includes(kw)))
          .reduce((s, o) => s + (o.bakerPct || 0), 0)
      : 0;

  // ── Flour handlers ────────────────────────────────────────────────────────

  const updateFlourName = useCallback((id: number, name: string) => {
    setFlours(prev => prev.map(f => (f.id === id ? { ...f, name } : f)));
  }, []);

  const updateFlourPct = useCallback((id: number, val: string) => {
    const v = parseFloat(val);
    setFlours(prev => prev.map(f => (f.id === id ? { ...f, bakerPct: isNaN(v) ? 0 : v } : f)));
  }, []);

  const addFlour = () => {
    setFlours(prev => [...prev, { id: uid(), name: 'Flour', bakerPct: 0 }]);
  };

  const removeFlour = (id: number) => {
    setFlours(prev => (prev.length > 1 ? prev.filter(f => f.id !== id) : prev));
  };

  // ── Other ingredient handlers ─────────────────────────────────────────────

  const updateOtherName = useCallback((id: number, name: string) => {
    setOthers(prev => prev.map(o => (o.id === id ? { ...o, name } : o)));
  }, []);

  const updateOtherPct = useCallback((id: number, val: string) => {
    const v = parseFloat(val);
    setOthers(prev => prev.map(o => (o.id === id ? { ...o, bakerPct: isNaN(v) ? 0 : v } : o)));
  }, []);

  const addOther = () => {
    setOthers(prev => [...prev, { id: uid(), name: 'Ingredient', bakerPct: 0 }]);
  };

  const removeOther = (id: number) => {
    setOthers(prev => prev.filter(o => o.id !== id));
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <CompanyLayout>
      <Container fluid style={{ maxWidth: 600, padding: '24px 16px' }}>

        {/* Page title */}
        <h5 style={{ fontWeight: 800, color: '#1e293b', marginBottom: 24 }}>
          Baker's Percentage Calculator
        </h5>

        {/* ── Top stats ─────────────────────────────────────────────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
            marginBottom: 20,
          }}
        >
          <div>
            <div style={labelStyle}>Total dough weight (grams)</div>
            <div style={readonlyBox}>{totalDoughWeight.toFixed(0)}</div>
          </div>
          <div>
            <div style={labelStyle}>Total hydration</div>
            <div style={readonlyBox}>{totalHydration.toFixed(1)}%</div>
          </div>
          <div>
            <div style={labelStyle}>Individual dough weight (grams)</div>
            <input
              type="number"
              min={0}
              style={inputBox}
              value={individualWeight || ''}
              onChange={e => {
                const v = parseFloat(e.target.value);
                setIndividualWeight(isNaN(v) ? 0 : v);
              }}
            />
          </div>
          <div>
            <div style={labelStyle}>QTY of loaves</div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1.5px solid #e2e8f0',
                borderRadius: '10px',
                overflow: 'hidden',
              }}
            >
              <button
                onClick={() => setQty(q => Math.max(1, q - 1))}
                style={{
                  background: '#f8fafc',
                  border: 'none',
                  borderRight: '1.5px solid #e2e8f0',
                  padding: '10px 14px',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  color: '#64748b',
                  flexShrink: 0,
                }}
              >
                −
              </button>
              <div
                style={{
                  flex: 1,
                  textAlign: 'center',
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  color: '#1e293b',
                  padding: '10px 0',
                }}
              >
                {qty}
              </div>
              <button
                onClick={() => setQty(q => q + 1)}
                style={{
                  background: '#f8fafc',
                  border: 'none',
                  borderLeft: '1.5px solid #e2e8f0',
                  padding: '10px 14px',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  color: '#64748b',
                  flexShrink: 0,
                }}
              >
                +
              </button>
            </div>
          </div>
        </div>

        {/* ── Divider ───────────────────────────────────────────────────── */}
        <hr style={{ borderColor: '#e2e8f0', margin: '20px 0' }} />

        {/* ── Recipe ingredients ────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h6 style={{ fontWeight: 800, color: '#1e293b', margin: 0, fontSize: '1.05rem' }}>
            Recipe ingredients
          </h6>
          <button
            onClick={() => setEditMode(e => !e)}
            style={{
              background: editMode ? '#f0fdf4' : 'none',
              border: editMode ? '1px solid #86efac' : 'none',
              borderRadius: 8,
              padding: '4px 8px',
              cursor: 'pointer',
              color: editMode ? '#059669' : '#94a3b8',
            }}
            title="Edit ingredient names"
          >
            <PencilIcon style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* ── Flour section ─────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0 6px', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ ...labelStyle, marginBottom: 0 }}>Flour</div>
          <div style={{ ...labelStyle, marginBottom: 0, width: 90, textAlign: 'center' }}>Baker's %</div>
          <div style={{ ...labelStyle, marginBottom: 0, width: 90, textAlign: 'center' }}>Grams</div>
        </div>

        {flours.map(flour => (
          <div
            key={flour.id}
            style={{
              display: 'grid',
              gridTemplateColumns: editMode ? '1fr auto auto auto' : '1fr auto auto',
              gap: '0 6px',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            {editMode ? (
              <input
                type="text"
                value={flour.name}
                onChange={e => updateFlourName(flour.id, e.target.value)}
                style={{ ...inputBox, textAlign: 'left', padding: '8px 12px' }}
              />
            ) : (
              <div
                style={{
                  ...inputBox,
                  textAlign: 'center',
                  fontWeight: 800,
                  fontSize: '1rem',
                  padding: '10px 8px',
                }}
              >
                {flour.name}
              </div>
            )}
            <input
              type="number"
              min={0}
              step={1}
              value={flour.bakerPct || ''}
              onChange={e => updateFlourPct(flour.id, e.target.value)}
              style={{ ...inputBox, width: 90 }}
            />
            <div style={{ ...readonlyBox, width: 90 }}>
              {gramsFor(flour.bakerPct).toFixed(1)}
            </div>
            {editMode && (
              <button
                onClick={() => removeFlour(flour.id)}
                disabled={flours.length === 1}
                style={{
                  background: 'none',
                  border: 'none',
                  color: flours.length === 1 ? '#cbd5e1' : '#ef4444',
                  cursor: flours.length === 1 ? 'not-allowed' : 'pointer',
                  padding: 4,
                }}
              >
                <TrashIcon style={{ width: 16, height: 16 }} />
              </button>
            )}
          </div>
        ))}

        {/* Add flour */}
        <button
          onClick={addFlour}
          style={{
            background: 'none',
            border: 'none',
            color: '#64748b',
            fontSize: '0.85rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 0',
            marginBottom: 12,
          }}
        >
          <PlusIcon style={{ width: 15, height: 15 }} />
          Add more
        </button>

        {/* ── Divider ───────────────────────────────────────────────────── */}
        <hr style={{ borderColor: '#e2e8f0', margin: '12px 0 16px' }} />

        {/* ── Other ingredients ─────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0 6px', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ ...labelStyle, marginBottom: 0 }}>Other ingredient</div>
          <div style={{ ...labelStyle, marginBottom: 0, width: 90, textAlign: 'center' }}>Baker's %</div>
          <div style={{ ...labelStyle, marginBottom: 0, width: 90, textAlign: 'center' }}>Grams</div>
        </div>

        {others.map(other => (
          <div
            key={other.id}
            style={{
              display: 'grid',
              gridTemplateColumns: editMode ? '1fr auto auto auto' : '1fr auto auto',
              gap: '0 6px',
              alignItems: 'center',
              marginBottom: 14,
            }}
          >
            {editMode ? (
              <input
                type="text"
                value={other.name}
                onChange={e => updateOtherName(other.id, e.target.value)}
                style={{ ...inputBox, textAlign: 'left', padding: '8px 12px' }}
              />
            ) : (
              <div style={{ fontWeight: 700, fontSize: '0.98rem', color: '#1e293b', padding: '4px 0' }}>
                {other.name}
              </div>
            )}
            <input
              type="number"
              min={0}
              step={0.1}
              value={other.bakerPct || ''}
              onChange={e => updateOtherPct(other.id, e.target.value)}
              style={{ ...inputBox, width: 90 }}
            />
            <div style={{ ...readonlyBox, width: 90 }}>
              {gramsFor(other.bakerPct).toFixed(1)}
            </div>
            {editMode && (
              <button
                onClick={() => removeOther(other.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ef4444',
                  cursor: 'pointer',
                  padding: 4,
                }}
              >
                <TrashIcon style={{ width: 16, height: 16 }} />
              </button>
            )}
          </div>
        ))}

        {/* Add other */}
        <button
          onClick={addOther}
          style={{
            background: 'none',
            border: 'none',
            color: '#64748b',
            fontSize: '0.85rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 0',
            marginBottom: 24,
          }}
        >
          <PlusIcon style={{ width: 15, height: 15 }} />
          Add more
        </button>

        {/* ── Total summary ─────────────────────────────────────────────── */}
        <div
          style={{
            background: '#f8fafc',
            borderRadius: 12,
            padding: '14px 18px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.82rem',
            color: '#64748b',
          }}
        >
          <span>Total baker's %</span>
          <span style={{ fontWeight: 800, color: '#1e293b', fontSize: '0.95rem' }}>
            {totalAllBakerPct.toFixed(1)}%
          </span>
        </div>

      </Container>
    </CompanyLayout>
  );
}
